import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { SiteDetailsTab, SiteDocumentsPhotosTab } from "./components";
import { PageHeader, PageHeaderWithBack } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { sitesService } from "@/api/services/sites.service";
// import type { SiteVersion, WorkItem } from "@/types/site.types"; // Version feature commented out
import styles from "./SiteEdit.module.css";
const TABS = [
    { id: "details", label: "Site Details" },
    { id: "documents", label: "Documents and Photos" },
];
export const SiteEdit = () => {
    const { siteId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState("details");
    const [site, setSite] = useState(null);
    // Version feature commented out
    // const [versions, setVersions] = useState<SiteVersion[]>([]);
    // const [selectedVersion, setSelectedVersion] = useState<string>("");
    // const [isVersionDropdownOpen, setIsVersionDropdownOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [creatingEstimate, setCreatingEstimate] = useState(false);
    const [error, setError] = useState(null);
    // const [versionWorkItems, setVersionWorkItems] = useState<WorkItem[] | null>(null);
    // const [isViewingVersion, setIsViewingVersion] = useState(false);
    // Ref to hold the save function from SiteDetailsTab
    const saveHandlerRef = useRef(null);
    // Ref to hold the create estimate function from SiteDetailsTab
    const createEstimateHandlerRef = useRef(null);
    const fetchSite = useCallback(async () => {
        if (!siteId)
            return;
        try {
            setLoading(true);
            setError(null);
            const response = await sitesService.getSite(siteId);
            setSite(response.data);
            // Version feature commented out
            // if (response.data.versions && response.data.versions.length > 0) {
            //   setVersions(response.data.versions);
            //   const currentVersion = response.data.versions[0];
            //   setSelectedVersion(currentVersion.id);
            //   setIsViewingVersion(false);
            //   setVersionWorkItems(null);
            // } else {
            //   setVersions([{
            //     id: "initial",
            //     versionNum: 1,
            //     name: "Version 1 (Current)",
            //     createdAt: new Date().toISOString(),
            //   }]);
            //   setSelectedVersion("initial");
            //   setIsViewingVersion(false);
            //   setVersionWorkItems(null);
            // }
        }
        catch (err) {
            setError("Failed to load site");
            console.error("Error fetching site:", err);
        }
        finally {
            setLoading(false);
        }
    }, [siteId]);
    useEffect(() => {
        fetchSite();
    }, [fetchSite]);
    const handleBackClick = () => {
        navigate(-1);
    };
    // Version feature commented out
    // const handleVersionChange = async (versionId: string) => {
    //   if (!siteId) return;
    //   setSelectedVersion(versionId);
    //   setIsVersionDropdownOpen(false);
    //   const isCurrentVersion = versions.length > 0 && versions[0].id === versionId;
    //   if (isCurrentVersion) {
    //     setIsViewingVersion(false);
    //     setVersionWorkItems(null);
    //   } else {
    //     try {
    //       setIsViewingVersion(true);
    //       const response = await sitesService.getVersion(siteId, versionId);
    //       if (response.data.workItemsSnapshot) {
    //         setVersionWorkItems(response.data.workItemsSnapshot);
    //       } else {
    //         setVersionWorkItems([]);
    //       }
    //     } catch (err) {
    //       console.error("Error loading version:", err);
    //       setIsViewingVersion(false);
    //       setVersionWorkItems(null);
    //     }
    //   }
    // };
    // const handleAddVersion = async () => {
    //   if (!siteId) return;
    //   try {
    //     const response = await sitesService.createVersion(siteId);
    //     const newVersion = response.data;
    //     const updatedVersions = versions.map((v, index) =>
    //       index === 0 ? { ...v, name: v.name.replace(" (Current)", "") } : v
    //     );
    //     setVersions([newVersion, ...updatedVersions]);
    //     setSelectedVersion(newVersion.id);
    //     setIsVersionDropdownOpen(false);
    //   } catch (err) {
    //     console.error("Error creating version:", err);
    //   }
    // };
    const handleSaveClick = async () => {
        if (saveHandlerRef.current) {
            setSaving(true);
            try {
                await saveHandlerRef.current();
                await fetchSite();
            }
            catch (err) {
                console.error("Error saving:", err);
            }
            finally {
                setSaving(false);
            }
        }
    };
    const handleCreateEstimateClick = async () => {
        if (createEstimateHandlerRef.current) {
            setCreatingEstimate(true);
            try {
                await createEstimateHandlerRef.current();
            }
            catch (err) {
                console.error("Error creating estimate:", err);
            }
            finally {
                setCreatingEstimate(false);
            }
        }
    };
    const handleCreateJobClick = () => {
        if (!siteId)
            return;
        const basePath = location.pathname.startsWith("/staff")
            ? "/staff"
            : "/admin";
        navigate(`${basePath}/jobs/add?siteId=${siteId}`);
    };
    // const getSelectedVersionName = () => {
    //   const version = versions.find((v) => v.id === selectedVersion);
    //   return version?.name || "Select Version";
    // };
    const renderTabContent = () => {
        if (loading) {
            return _jsx("div", { className: styles.loading, children: "Loading site data..." });
        }
        if (error) {
            return _jsx("div", { className: styles.error, children: error });
        }
        if (!site) {
            return _jsx("div", { className: styles.error, children: "Site not found" });
        }
        switch (activeTab) {
            case "details":
                return (_jsx(SiteDetailsTab, { site: site, saveHandlerRef: saveHandlerRef, createEstimateHandlerRef: createEstimateHandlerRef, versionWorkItems: null, isViewingVersion: false }));
            case "documents":
                return _jsx(SiteDocumentsPhotosTab, {});
            default:
                return null;
        }
    };
    return (_jsxs("div", { className: styles.container, children: [_jsx(PageHeader, { userName: user?.name || "User", userEmail: user?.email || "" }), _jsx(PageHeaderWithBack, { title: "Edit Site", subtitle: site?.siteName ?? siteId ?? undefined, onBack: handleBackClick, breadcrumb: "Sites/Edit Site" }), _jsx("nav", { className: styles.navTabs, children: _jsx("div", { className: styles.tabList, role: "tablist", children: TABS.map((tab) => (_jsx("button", { className: `${styles.tabButton} ${activeTab === tab.id ? styles.active : ""}`, onClick: () => setActiveTab(tab.id), role: "tab", "aria-selected": activeTab === tab.id, children: tab.label }, tab.id))) }) }), _jsxs("div", { className: styles.contentArea, children: [renderTabContent(), activeTab === "details" && !loading && site && (_jsxs("div", { className: styles.summaryActions, children: [_jsx("button", { className: styles.saveButton, onClick: handleSaveClick, disabled: saving, children: saving ? "Saving..." : "Save" }), _jsx("button", { className: styles.createEstimateButton, onClick: handleCreateJobClick, children: "Create Job" }), _jsx("button", { className: styles.createEstimateButton, onClick: handleCreateEstimateClick, disabled: creatingEstimate, children: creatingEstimate ? "Creating..." : "Create Estimate" })] }))] })] }));
};
